import Widget from "@arcgis/core/widgets/Widget";
import { messageBundle, tsx } from "@arcgis/core/widgets/support/widget";
import { property, subclass } from "@arcgis/core/core/accessorSupport/decorators";

import { VNode } from 'src/interfaces/interfaces';
import InstantAppLandingViewModel from './InstantAppLanding/InstantAppLandingViewModel';
import Portal from '../utils/Portal/Portal';

import "./InstantAppLanding/styles/InstantAppLanding.scss";

import NavBar from "./NavBar";
import ContentArea from "./ContentArea";
import { when } from "@arcgis/core/core/reactiveUtils";

import Telemetry from "@esri/telemetry";

export interface InstantAppLandingProperties extends __esri.WidgetProperties {
	portal: Portal;
	appConfig: any;
	telemetry: Telemetry;
}

@subclass("Landing.ui.InstantAppLanding")
export default class InstantAppLanding extends Widget {

	@property()
	portal: Portal;

	@property()
	telemetry: Telemetry;

	@property()
	appConfig: any;

	@property()
	viewModel: InstantAppLandingViewModel;

	@property()
	navBar: NavBar = new NavBar();

	@property()
	contentArea: ContentArea = new ContentArea();

	@property()
	_globalNavDOM: VNode;
	@property()
	_navBarDOM: VNode[];
	@property()
	_contentAreaDOM: VNode;

	@property()
	@messageBundle("dist/assets/t9n/App/App")
	appMessages;

	@property()
	@messageBundle("dist/assets/t9n/widgets/GlobalNav/GlobalNav")
	globalNavMessages;

	/** State tracking to eliminate unnecessary Height setting */
	private _previousContentAreaHeight: number;

	constructor(args: InstantAppLandingProperties) {
		super(args);
	}

	postInitialize() {
		this.viewModel = new InstantAppLandingViewModel({
			appConfig: this.appConfig,
			portal: this.portal,
		});

		when(() => this?.appMessages !== undefined, () => {
			this.viewModel.appMessages = this.appMessages;
		}, {
            initial: true,
						once: true
        });

		when(() => this?.globalNavMessages !== undefined, () => {
			this.viewModel.globalNavMessages = this.globalNavMessages;
		}, {
            initial: true,
						once: true
        });

		this.navBar.portal = this.portal;
		this.contentArea.portal = this.portal;

		this.contentArea.navbarVM = this.navBar.viewModel;
		this.contentArea.telemetry = this.telemetry;

		this.own(
			[
				when(() => this?.navBar || this?.contentArea, () => {
					this.scheduleRender();
				}),
				when(() => this?._globalNavDOM || this?._navBarDOM, () => {
					this._handleAppHeightResize();
				})
			]
		);

		window.addEventListener("resize", this._handleAppHeightResize.bind(this));
	}

	render() {
		this._globalNavDOM = this._renderGlobalNav();
		this._navBarDOM = this.navBar?.render();
		this._contentAreaDOM = this.contentArea?.render();

		return (
			<div>
				{this._globalNavDOM}

				<div class="app-layout">
					{this._navBarDOM}
					{this._contentAreaDOM}
				</div>
			</div>
		);
	}

	private _renderGlobalNav(): VNode {
		return (
			<div
				id={"global-nav"}
				class={this.classes("js-global-nav", "global-nav")}
				afterCreate={this._createGlobalNav.bind(this)}
			/>
		);
	}

	private _createGlobalNav(): void {
		this.viewModel.createGlobalNav();
	}

	private _OFFSET_WITH_FILTER_CHIPS = 19;
	private _OFFSET_WITHOUT_FILTER_CHIPS = 0;

	private _handleAppHeightResize(): void {
		const windowHeight: number = window.innerHeight;
		const globalNavHeight: number = (this._globalNavDOM as any)?.domNode?.offsetHeight;

		let domNodesDefined = 0;
		const navbarHeight: number = (this._navBarDOM?.reduce((acc: number, dom: any) => { // sum heights of all _navBarDOM 
			const curr = (dom?.domNode?.offsetHeight != null ? dom?.domNode?.offsetHeight : 0);
			if(curr > 0) domNodesDefined++;
			return curr + acc;
		}, 0));

		const totalHeightOffset: number = globalNavHeight
			+ navbarHeight + (domNodesDefined === 2 ? this._OFFSET_WITH_FILTER_CHIPS : this._OFFSET_WITHOUT_FILTER_CHIPS);

		if (totalHeightOffset == null || Number.isNaN(totalHeightOffset)) { return; }

		const contentAreaHeight = windowHeight - totalHeightOffset;

		if (contentAreaHeight !== this._previousContentAreaHeight) { // Get rid of wasteful height updates
			this._previousContentAreaHeight = contentAreaHeight;
			this.contentArea.setHeight(contentAreaHeight);
		}
	}

}